import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import ItemPost from '../components/ItemPost';
import { ReactComponent as ArrowLeft } from '../img/Rectangle2.svg';
import { ReactComponent as ArrowLeftGreen } from '../img/Rectangle2green.svg';
import { ReactComponent as ArrowLeftRed } from '../img/Rectangle2red.svg';
import { ReactComponent as HorizontalArrows } from '../img/mingcute_move-fill.svg';



const ItemTypes = {
  TABLE_ROW: 'TABLE_ROW',
};


const DraggableTableRow = ({ record, index, moveRow, status, onDropCrossColumn }) => {
  const ref = useRef(null);
  const [isOver, setIsOver] = useState(false);

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.TABLE_ROW,
    item: { record, index, status },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: ItemTypes.TABLE_ROW,
    hover(item, monitor) {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      if (item.status !== status) return;

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      moveRow(dragIndex, hoverIndex, status);
      item.index = hoverIndex;
    },
    collect: (monitor) => {
      setIsOver(monitor.isOver({ shallow: true }));
    },
    drop(item, monitor) {
      const statusOfDraggedItem = item.status;
      setIsOver(false);
      if (statusOfDraggedItem !== status) {
        if (onDropCrossColumn) {
          onDropCrossColumn(status, item, index);
        }
        return { handled: true };
      }
      return undefined;
    },
  });

  useEffect(() => {
    drag(drop(ref));
    return () => setIsOver(false);
  }, [drag, drop]);

  const color = record[8]?.stringValue;
  const link = record[0]?.longValue || 'ID not available';
  const { pathname } = useLocation();

  return (
    <div
      ref={ref}
      className={`table-row${isDragging ? ' dragging' : ''}${isOver ? ' hovering' : ''}`}
      style={{
        opacity: isDragging ? 0.3 : 1,
        background: isOver ? '#e3f7ff' : isDragging ? '#b9e9f8' : undefined,
        transition: 'background 0.2s, opacity 0.2s',
        cursor: 'move'
      }}
    >
      <a href={`${pathname}/story/${link}`}>
        <section>
          <div><h2>{record[1]?.stringValue}</h2></div>
          <div>
            <small>
              {record[2]?.stringValue?.length > 60
                ? `${record[2]?.stringValue.substring(0, 60)}...`
                : record[2]?.stringValue}
            </small>
          </div>
          <div>
            <HorizontalArrows style={{ height: '25px', width: '25px' }} />
          </div>
        </section>
        <section>
          <div className="triangle">
            {color === 'green'
              ? <ArrowLeftGreen style={{ height: '115px', width: '115px' }} />
              : color === 'red'
                ? <ArrowLeftRed style={{ height: '115px', width: '115px' }} />
                : <ArrowLeft style={{ height: '115px', width: '115px' }} />}
          </div>
        </section>
      </a>
    </div>
  );
};

function StatusColumn({ status, records, onDrop, moveRow, onDropSameColumn }) {
  const dropTargetRef = useRef(null);
  const [isOver, setIsOver] = useState(false);

  const [, drop] = useDrop({
    accept: ItemTypes.TABLE_ROW,
    hover(item, monitor) {
      if (!monitor.isOver()) {
        return;
      }

      const statusOfDraggedItem = item.status;

      if (status !== statusOfDraggedItem) {
        // Add a CSS class to indicate hovering over the column
        setIsOver(true);
      } else {
        setIsOver(false);
      }
    },
    drop(item, monitor) {
      if (monitor.didDrop()) {
        setIsOver(false);
        return;
      }

      const statusOfDraggedItem = item.status;

      if (status !== statusOfDraggedItem) {
        // If dropped on a different status column, trigger onDrop
        onDrop(status, item, null);
      } else if (onDropSameColumn) {
        onDropSameColumn(status, records);
      }

      // Remove the hovering CSS class when dropped
      setIsOver(false);
    },
  });

  drop(dropTargetRef);

  return (
    <div className={`itemcolumn dropzone ${isOver ? 'hovering' : ''}`} ref={dropTargetRef}>
      <h3 style={{ cursor: 'default' }}>{status}</h3>
      <div className="itemblock">
        {records.map((record, index) => (
          <DraggableTableRow
            key={index}
            index={index}
            record={record}
            moveRow={moveRow}
            status={status}
            onDropCrossColumn={onDrop}
          />
        ))}
      </div>
    </div>
  );
}

function NewsFeed() {
  const [records, setRecords] = useState([]);
  const [backlog, setBacklog] = useState([]);
  const [inProgress, setInProgress] = useState([]);
  const [maxItems, setMaxItems] = useState(20); // Add maxItems state
  const [completed, setCompleted] = useState([]);
  const lastInteractionTime = useRef(0);
  const { pathname } = useLocation();
  const idFatherProyect = pathname.startsWith("/proyect/")
    ? pathname.split("/").pop() // Get the last part of the URL
    : null;


  const fetchData = useCallback(async () => {
    try {
      let emailId = localStorage.getItem("emailId");
      if (emailId) {
        emailId = emailId.replace(/^"(.*)"$/, '$1');
      }
      if (!emailId) {
        console.error('Email ID not found in local storage');
        return;
      }

      const filterValueEmail = encodeURIComponent(emailId);
      //const response = await fetch(`https://nstjfqisb1.execute-api.us-east-1.amazonaws.com/getItem?userID=${filterValueEmail}&parentId=9`); // Add your API endpoint here
      const response = await fetch(`https://nstjfqisb1.execute-api.us-east-1.amazonaws.com/getItem?parentId=${idFatherProyect}`); // Add your API endpoint here
      if (!response.ok) {
        throw new Error('Failed to fetch records');
      }
      const jsonData = await response.json();

      if (Array.isArray(jsonData.result)) {
        setRecords(jsonData.result);
      } else if (jsonData.message) {
        console.error('Error message from server:', jsonData.message);
      } else {
        console.error('Invalid data format:', jsonData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => {
      fetchData();
    }, 2000);

    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    const sortRecordsByPosition = (records) => {
      return records.sort((a, b) => a[9]?.longValue - b[9]?.longValue);
    };

    if (records) {
      if (Date.now() - lastInteractionTime.current < 3000) {
        return; // Ignore sorting from backend for 3 seconds after an update/drag to avoid jumping back
      }

      const backlogRecords = sortRecordsByPosition(records.filter(record => record[7]?.stringValue === 'Backlog')).slice(0, maxItems);
      const inProgressRecords = sortRecordsByPosition(records.filter(record => record[7]?.stringValue === 'In Progress')).slice(0, maxItems);
      const completedRecords = sortRecordsByPosition(records.filter(record => record[7]?.stringValue === 'Completed')).slice(0, maxItems);

      setBacklog(backlogRecords);
      setInProgress(inProgressRecords);
      setCompleted(completedRecords);
    }
  }, [records, maxItems]); // Add maxItems as dependency

  const handleDrop = async (status, item, targetIndex = null) => {
    lastInteractionTime.current = Date.now();
    const recordId = item.record[0]?.longValue;
    const oldStatus = item.status;
    const newStatus = status;

    let colorStatus = "grey";
    if (newStatus === "In Progress" || newStatus === "Completed") {
      colorStatus = "green";
    }

    const updatedState = {
      backlog: [...backlog],
      'In Progress': [...inProgress],
      completed: [...completed]
    };

    let oldArray, newArray;
    if (oldStatus === 'Backlog') oldArray = updatedState.backlog;
    else if (oldStatus === 'In Progress') oldArray = updatedState['In Progress'];
    else if (oldStatus === 'Completed') oldArray = updatedState.completed;

    if (newStatus === 'Backlog') newArray = updatedState.backlog;
    else if (newStatus === 'In Progress') newArray = updatedState['In Progress'];
    else if (newStatus === 'Completed') newArray = updatedState.completed;

    if (oldArray && newArray) {
      const oldIndex = oldArray.findIndex(r => r[0]?.longValue === recordId);
      if (oldIndex > -1) {
        oldArray.splice(oldIndex, 1);
      }

      const newRecord = [...item.record];
      newRecord[7] = { stringValue: newStatus };
      newRecord[8] = { stringValue: colorStatus };

      if (targetIndex !== null && targetIndex >= 0) {
        newArray.splice(targetIndex, 0, newRecord);
      } else {
        newArray.push(newRecord);
      }

      setBacklog(updatedState.backlog);
      setInProgress(updatedState['In Progress']);
      setCompleted(updatedState.completed);

      await updatePositions(oldArray);
      await updatePositions(newArray);
    }

    try {
      const response = await fetch('https://nstjfqisb1.execute-api.us-east-1.amazonaws.com/updateItem', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: recordId, status: newStatus, position: newArray ? newArray.length : 1, color: colorStatus }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to update status:', response.status, errorText);
        throw new Error(`Failed to update status: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const updatePositions = async (records) => {
    const updatedRecords = records.map((record, index) => ({
      id: record[0]?.longValue,
      position: index + 1,
    }));

    //console.log('Sending updated records:', JSON.stringify(updatedRecords));

    try {
      const response = await fetch('https://nstjfqisb1.execute-api.us-east-1.amazonaws.com/updateItem', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedRecords),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Failed to update positions:', errorData);
      }
    } catch (error) {
      console.error('Error updating positions:', error);
    }
  };

  const moveRow = (dragIndex, hoverIndex, status) => {
    lastInteractionTime.current = Date.now();
    //console.log(`Moving row within ${status} from ${dragIndex} to ${hoverIndex}`);
    const newRecords = { backlog, inProgress, completed };

    const moveArrayItem = (array, fromIndex, toIndex) => {
      const item = array.splice(fromIndex, 1)[0];
      array.splice(toIndex, 0, item);
    };

    switch (status) {
      case 'Backlog':
        moveArrayItem(newRecords.backlog, dragIndex, hoverIndex);
        setBacklog([...newRecords.backlog]);
        break;
      case 'In Progress':
        moveArrayItem(newRecords.inProgress, dragIndex, hoverIndex);
        setInProgress([...newRecords.inProgress]);
        break;
      case 'Completed':
        moveArrayItem(newRecords.completed, dragIndex, hoverIndex);
        setCompleted([...newRecords.completed]);
        break;
      default:
        break;
    }
  };

  const handleDropSameColumn = (dropStatus, records) => {
    lastInteractionTime.current = Date.now();
    updatePositions(records);
  };



  let type = "Story";
  let parent = idFatherProyect;
  let status = "Backlog";
  let color = "grey";

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="itemlist">
        <main>
          <StatusColumn status="Backlog" records={backlog} onDrop={handleDrop} moveRow={moveRow} onDropSameColumn={handleDropSameColumn} />
          <StatusColumn status="In Progress" records={inProgress} onDrop={handleDrop} moveRow={moveRow} onDropSameColumn={handleDropSameColumn} />
          <StatusColumn status="Completed" records={completed} onDrop={handleDrop} moveRow={moveRow} onDropSameColumn={handleDropSameColumn} />
        </main>
        <button
          style={{ margin: '20px auto', display: 'block' }}
          onClick={() => setMaxItems(prev => prev + 20)}
        >
          See more
        </button>
        <ItemPost type={type} parent={parent} status={status} color={color} />
      </div>
    </DndProvider>
  );
}

export default NewsFeed;
